/**
 * VenueFlow DropArea Component
 * 
 * Drag-and-drop file upload zone with click-to-browse.
 * Mac/Safari compatible - drag handlers on both container and inner area.
 */
import React, { useRef, useState, useCallback } from "react";

// ============================================================
// STYLES
// ============================================================
const styles = {
  container: {
    border: "2px dashed rgba(47, 214, 181, 0.28)",
    borderRadius: 10,
    padding: 14,
    background: "#f3f4ff",
    textAlign: "left",
    transition: "all 0.2s",
    position: "relative",
  },
  containerHover: {
    borderColor: "rgba(47, 214, 181, 1)",
    background: "rgba(47, 214, 181, 0.15)",
  },
  containerDragOver: {
    borderColor: "rgba(231, 255, 24, 0.6)",
    background: "rgba(47, 214, 181, 0.25)",
  },
  dropArea: {
    marginTop: 2,
    padding: 14,
    borderRadius: 8,
    background: "#e5e7eb",
    color: "#374151",
    cursor: "pointer",
    fontSize: 13,
    textAlign: "center",
    transition: "all 0.2s",
    userSelect: "none",
  },
  dropAreaDragOver: {
    background: "rgba(47, 214, 181, 0.35)",
    border: "1px solid rgba(231, 255, 24, 0.6)",
    color: "#111827",
  },
  hiddenInput: {
    display: "none",
  },
};

// ============================================================
// DROP AREA COMPONENT
// ============================================================
export function DropArea({ onFiles, accept, multiple = true, disabled }) {
  const inputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  
  // Counter to handle nested drag events (Mac fix)
  const dragCounter = useRef(0);

  const handleClick = useCallback(() => {
    if (!disabled && inputRef.current) {
      inputRef.current.click();
    }
  }, [disabled]);

  // Prevent default on ALL drag events (required for Mac)
  const preventDefaults = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragEnter = useCallback(
    (e) => {
      preventDefaults(e);
      dragCounter.current++;
      if (!disabled && e.dataTransfer?.items?.length) {
        setIsDragOver(true);
      }
    },
    [disabled, preventDefaults]
  );

  const handleDragOver = useCallback(
    (e) => {
      preventDefaults(e);
      if (!disabled) {
        setIsDragOver(true);
      }
    },
    [disabled, preventDefaults]
  );

  const handleDragLeave = useCallback(
    (e) => {
      preventDefaults(e);
      dragCounter.current--;
      if (dragCounter.current === 0) {
        setIsDragOver(false);
      }
    },
    [preventDefaults]
  );

  const handleDrop = useCallback(
    (e) => {
      preventDefaults(e);
      dragCounter.current = 0;
      setIsDragOver(false);
      
      if (disabled) return;
      
      const files = e.dataTransfer?.files;
      if (files?.length) {
        onFiles([...files]);
      }
    },
    [disabled, onFiles, preventDefaults]
  );

  const handleChange = useCallback(
    (e) => {
      const files = e.target.files;
      if (files?.length) {
        onFiles([...files]);
      }
      // Reset input so the same file can be selected again
      e.target.value = "";
    },
    [onFiles]
  );

  return (
    <div
      style={{
        ...styles.container,
        ...(isHovered ? styles.containerHover : {}),
        ...(isDragOver ? styles.containerDragOver : {}),
        ...(disabled ? { opacity: 0.5, cursor: "not-allowed" } : {}),
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      // Attach drag handlers to OUTER container (Mac fix)
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div
        style={{
          ...styles.dropArea,
          ...(isDragOver ? styles.dropAreaDragOver : {}),
        }}
        onClick={handleClick}
        // Also keep handlers on inner area for visual feedback
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        Drop file{multiple ? "s" : ""} here or click to browse
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleChange}
        style={styles.hiddenInput}
      />
    </div>
  );
}

export default DropArea;
